import { useCallback, useMemo, useState } from 'react';
import * as React from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { CirclePlus, X } from 'lucide-react';
import { ArrayPath, FieldArray, Path, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useScrollOverflow } from '@/hooks/scroll/use-scroll-overflow';
import { useFieldArrayKeyboard } from '@/hooks/use-field-array-keyboard';
import { cn } from '@/lib/utils';
import { logger } from '@/utils/logger/logger';

import { AddEntitiesModalProps, EntitiesFormData, EntityField } from './types';

const TAG = 'AddEntitiesModal:';
const DEFAULT_ARRAY_FIELD_NAME = 'entities';

export function AddOnboardingEntitiesModal<T extends EntitiesFormData, U extends object>({
  schema,
  fields,
  mutation,
  transformData,
  defaultEntityValues,
  baseKey = '',
  buttonLabel,
  modalTitle,
  addMoreLabel,
  submitLabel,
  onSuccess,
  arrayFieldName = DEFAULT_ARRAY_FIELD_NAME,
}: AddEntitiesModalProps<T, U>) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { isValid },
  } = useForm<T>({
    resolver: zodResolver(schema),
    defaultValues: defaultEntityValues,
    mode: 'onBlur',
  });

  const {
    fields: formFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: arrayFieldName as ArrayPath<T>,
  });

  const { ref: scrollRef, isScrollable } = useScrollOverflow([formFields.length]);

  /** Extract a single entity default from the form defaults for appending new rows */
  const singleEntityDefault = useMemo(() => {
    const defaults = defaultEntityValues as Record<string, unknown[]>;
    const arrayDefaults = defaults[arrayFieldName];
    return (arrayDefaults?.[0] ?? {}) as FieldArray<T, ArrayPath<T>>;
  }, [defaultEntityValues, arrayFieldName]);

  const onClose = useCallback(
    (isOpen: boolean) => {
      reset();
      setOpen(isOpen);
    },
    [reset]
  );

  const onSubmit = useCallback(
    async (data: T) => {
      try {
        setIsSubmitting(true);
        const transformedData = transformData(data);
        await mutation(transformedData);
        onClose(false);
        const createdCount = (data[arrayFieldName] ?? []).length;
        onSuccess?.(createdCount);
      } catch (error) {
        // Error handling is done in the mutation hook
        logger.error(TAG, 'Failed to add entities', { error });
      } finally {
        setIsSubmitting(false);
      }
    },
    [transformData, mutation, onSuccess, onClose, arrayFieldName]
  );

  const appendEntity = useCallback(() => {
    append(singleEntityDefault);
  }, [append, singleEntityDefault]);

  /** Trigger array-level validation to catch duplicates on field change */
  const triggerArrayValidation = useCallback(() => {
    trigger(arrayFieldName as Path<T>);
  }, [trigger, arrayFieldName]);

  const keepFieldFocus = useCallback((event: React.MouseEvent) => event.preventDefault(), []);

  const { formRef, handleKeyDown } = useFieldArrayKeyboard({
    rowCount: formFields.length,
    onAppendRow: appendEntity,
    onSubmit: useCallback(() => handleSubmit(onSubmit)(), [handleSubmit, onSubmit]),
    arrayFieldName,
    firstFieldName: fields[0]?.name ?? '',
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogTrigger asChild>
        <Button type="button" size="sm" className="w-auto">
          <CirclePlus />
          {buttonLabel}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="flex max-h-[85vh] flex-col gap-0"
        closeButtonProps={{ onMouseDown: keepFieldFocus }}
      >
        <DialogHeader className={cn('shrink-0 pb-4', isScrollable && 'border-b')}>
          <DialogTitle>{modalTitle}</DialogTitle>
        </DialogHeader>

        <form
          ref={formRef}
          onSubmit={e => {
            e.stopPropagation();
            handleSubmit(onSubmit)(e);
          }}
          onKeyDown={handleKeyDown}
          className="flex flex-col overflow-hidden"
        >
          <div ref={scrollRef} className="space-y-4 overflow-y-auto px-1 py-4">
            {formFields.map((field, index) => (
              <div key={baseKey + field.id} className="flex items-start gap-1">
                <div className="flex w-full items-start gap-4">
                  {fields.map((entityField: EntityField<T>) =>
                    entityField.renderField ? (
                      <div
                        key={`${baseKey}-${field.id}-${entityField.name}`}
                        className="w-full min-w-0"
                      >
                        {entityField.renderField({
                          control,
                          name: `${arrayFieldName}.${index}.${entityField.name}` as Path<T>,
                          index,
                        })}
                      </div>
                    ) : (
                      <FormField
                        key={`${baseKey}-${field.id}-${entityField.name}`}
                        control={control}
                        name={`${arrayFieldName}.${index}.${entityField.name}` as Path<T>}
                        render={({ field: formField, fieldState: { error } }) => (
                          <FormItem className="flex w-full min-w-0 flex-col gap-2.5 space-y-0">
                            <FormLabel>{entityField.label}</FormLabel>
                            <FormControl>
                              {/*// @ts-ignore*/}
                              <Input
                                {...formField}
                                placeholder={entityField.placeholder}
                                onChange={e => {
                                  formField.onChange(e);
                                  triggerArrayValidation();
                                }}
                                className={cn(
                                  error &&
                                    'border-2 border-destructive focus-visible:ring-destructive'
                                )}
                              />
                            </FormControl>
                            <FormMessage>{error?.message}</FormMessage>
                          </FormItem>
                        )}
                      />
                    )
                  )}
                </div>
                {formFields.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="mt-6 shrink-0"
                    onClick={() => {
                      remove(index);
                      triggerArrayValidation();
                    }}
                    disabled={index === 0}
                    style={{ visibility: index === 0 ? 'hidden' : 'visible' }}
                  >
                    <X />
                  </Button>
                )}
              </div>
            ))}

            <Button
              variant="ghost"
              type="button"
              onClick={() => appendEntity()}
              className="flex items-center gap-1 text-sm"
            >
              <CirclePlus />
              {addMoreLabel}
            </Button>
          </div>

          <DialogFooter className={cn('shrink-0 pt-4', isScrollable && 'border-t')}>
            <DialogClose asChild>
              <Button type="button" variant="outline" onMouseDown={keepFieldFocus}>
                {t('close')}
              </Button>
            </DialogClose>
            <ButtonLoadable type="submit" isLoading={isSubmitting} disabled={!isValid}>
              {submitLabel}
            </ButtonLoadable>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
